import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import type { CreateCustomInvoiceRequest } from 'shared';

interface CustomInvoiceDialogProps {
  visible: boolean;
  onHide: () => void;
  onCreate: (data: CreateCustomInvoiceRequest) => Promise<void>;
  loading: boolean;
}

interface MemberOption {
  id: string;
  memberNumber: string;
  firstName: string;
  lastName: string;
  membershipFee: number | null;
  membershipStatus: string;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export const CustomInvoiceDialog: React.FC<CustomInvoiceDialogProps> = ({
  visible,
  onHide,
  onCreate,
  loading,
}) => {
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [invoiceYear, setInvoiceYear] = useState(new Date().getFullYear());
  const [invoiceDate, setInvoiceDate] = useState<Date>(new Date());
  const [paymentDeadline, setPaymentDeadline] = useState<Date>(addDays(new Date(), 30));
  const [purpose, setPurpose] = useState('');
  const [notes, setNotes] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (visible) {
      loadMembers();
      resetForm();
    }
  }, [visible]);

  useEffect(() => {
    setPaymentDeadline(addDays(invoiceDate, 30));
  }, [invoiceDate]);

  const resetForm = () => {
    setSelectedMemberId(null);
    setAmount(null);
    setInvoiceYear(new Date().getFullYear());
    setInvoiceDate(new Date());
    setPaymentDeadline(addDays(new Date(), 30));
    setPurpose('');
    setNotes('');
    setErrors({});
  };

  const loadMembers = async () => {
    setLoadingMembers(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/members`, {
        params: { limit: 1000 },
      });
      setMembers(response.data.data?.members || []);
    } catch (error) {
      console.error('Failed to load members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleMemberChange = (memberId: string) => {
    setSelectedMemberId(memberId);
    const member = members.find((m) => m.id === memberId);
    if (member?.membershipFee) {
      setAmount(parseFloat(member.membershipFee.toString()));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedMemberId) {
      newErrors.member = 'Please select a member';
    }
    if (!amount || amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }
    if (!purpose.trim()) {
      newErrors.purpose = 'Purpose is required';
    }
    if (purpose.length > 200) {
      newErrors.purpose = 'Purpose must be 200 characters or less';
    }
    if (paymentDeadline < invoiceDate) {
      newErrors.paymentDeadline = 'Payment deadline must be on or after invoice date';
    }
    if (notes.length > 500) {
      newErrors.notes = 'Notes must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const data: CreateCustomInvoiceRequest = {
      memberId: selectedMemberId!,
      amount: amount!,
      invoiceDate: invoiceDate.toISOString(),
      paymentDeadline: paymentDeadline.toISOString(),
      invoiceYear,
      purpose: purpose.trim(),
      notes: notes.trim() || undefined,
    };

    await onCreate(data);
  };

  const isValid =
    selectedMemberId && amount && amount > 0 && purpose.trim() && paymentDeadline >= invoiceDate;

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
        disabled={loading}
      />
      <Button
        label="Create Invoice"
        icon="pi pi-check"
        onClick={handleSubmit}
        disabled={!isValid || loading}
        loading={loading}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Create Custom Invoice"
      className="w-11/12 max-w-4xl"
      modal
      draggable={false}
      resizable={false}
      footer={footer}
      data-component="custom-invoice-dialog"
    >
      <div className="space-y-6">
        <Message
          severity="info"
          text="Create a custom invoice for special cases (events, sponsorships, etc.)"
          className="w-full"
        />

        {/* Member & Year Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="member" className="block text-sm font-medium text-gray-700 mb-2">
              Member *
            </label>
            <Dropdown
              id="member"
              value={selectedMemberId}
              options={members}
              onChange={(e) => handleMemberChange(e.value)}
              optionLabel={(m: MemberOption) =>
                `${m.memberNumber} - ${m.firstName} ${m.lastName} (${m.membershipStatus})`
              }
              optionValue="id"
              filter
              filterBy="memberNumber,firstName,lastName"
              placeholder="Select member..."
              className="w-full"
              disabled={loadingMembers || loading}
              loading={loadingMembers}
            />
            {errors.member && (
              <small className="text-red-500 mt-1 block">{errors.member}</small>
            )}
          </div>

          <div>
            <label htmlFor="invoiceYear" className="block text-sm font-medium text-gray-700 mb-2">
              Invoice Year *
            </label>
            <InputNumber
              id="invoiceYear"
              value={invoiceYear}
              onValueChange={(e) => setInvoiceYear(e.value!)}
              min={2000}
              max={2100}
              useGrouping={false}
              className="w-full"
              disabled={loading}
            />
          </div>
        </div>

        {/* Purpose - Full Width */}
        <div>
          <label htmlFor="purpose" className="block text-sm font-medium text-gray-700 mb-2">
            Purpose / Description *
          </label>
          <InputText
            id="purpose"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="e.g., Event participation fee, Sponsorship..."
            className="w-full"
            maxLength={200}
            disabled={loading}
          />
          <small className="block mt-1 text-gray-500 text-xs">
            {purpose.length}/200 characters - Visible on invoice PDF and email
          </small>
          {errors.purpose && (
            <small className="text-red-500 mt-1 block">{errors.purpose}</small>
          )}
        </div>

        {/* Amount & Dates Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
              Amount (EUR) *
            </label>
            <InputNumber
              id="amount"
              value={amount}
              onValueChange={(e) => setAmount(e.value)}
              mode="currency"
              currency="EUR"
              locale="de-DE"
              minFractionDigits={2}
              min={0.01}
              className="w-full"
              disabled={loading}
            />
            {errors.amount && (
              <small className="text-red-500 mt-1 block">{errors.amount}</small>
            )}
          </div>

          <div>
            <label htmlFor="invoiceDate" className="block text-sm font-medium text-gray-700 mb-2">
              Invoice Date *
            </label>
            <Calendar
              id="invoiceDate"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.value as Date)}
              dateFormat="dd.mm.yy"
              className="w-full"
              showIcon
              disabled={loading}
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="paymentDeadline" className="block text-sm font-medium text-gray-700 mb-2">
              Payment Deadline *
            </label>
            <Calendar
              id="paymentDeadline"
              value={paymentDeadline}
              onChange={(e) => setPaymentDeadline(e.value as Date)}
              minDate={invoiceDate}
              dateFormat="dd.mm.yy"
              className="w-full"
              showIcon
              disabled={loading}
            />
            {errors.paymentDeadline && (
              <small className="text-red-500 mt-1 block">{errors.paymentDeadline}</small>
            )}
          </div>
        </div>

        {/* Internal Notes - Full Width */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
            Internal Notes
          </label>
          <InputTextarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Optional notes for internal use (not visible on invoice)..."
            className="w-full"
            disabled={loading}
          />
          <small className="block mt-1 text-gray-500 text-xs">
            {notes.length}/500 characters - Internal only, not shown on PDF or email
          </small>
          {errors.notes && (
            <small className="text-red-500 mt-1 block">{errors.notes}</small>
          )}
        </div>
      </div>
    </Dialog>
  );
};
